import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ChevronDown, ChevronUp, Package } from 'lucide-react';
import POComponentItem from './POComponentItem';
import RacikanPOSection from './RacikanPOSection';
import MissingItemsAlert from './MissingItemsAlert';
import { toast } from 'sonner';

const CORE_CATEGORIES = ['botol', 'bibit', 'tutup', 'spray', 'bahan_cair', 'tambahan'];

export const makeComp = (category) => ({
  category,
  inventory_item_id: '',
  item_name: '',
  vendor: '',
  qty_needed: 0,
  stock_available: 0,
  unit: 'pcs',
  status: 'pending',
  item_notes: '',
  is_integrated: true,
  _raw_stock: undefined,
});

const calcStatus = (stok, needed, isFromClient) => {
  if (isFromClient) {
    if (needed > 0 && stok >= needed) return 'ready_client';
    return 'pending_client';
  }
  if (!needed || needed <= 0) return 'pending';
  return stok >= needed ? 'ready' : 'need_buy';
};

/**
 * ProductItemForm — satu card product dalam PO
 * Props: product, onChange, onRemove, inventoryItems, findInv, index
 */
export default function ProductItemForm({ product, onChange, onRemove, inventoryItems, findInv, index, priorUsageMap = {}, brandName = '' }) {
  const [collapsed, setCollapsed] = useState(false);
  const [missingItems, setMissingItems] = useState([]);

  const qty = Number(product.target_qty) || 0;
  const ukuranMl = Number(product.ukuran_botol_ml) || 0;

  const calcBibitQty = (persentase) => {
    if (!ukuranMl || !qty) return 0;
    return +(((parseFloat(String(persentase).replace(',', '.')) || 0) / 100) * ukuranMl * qty / 1000).toFixed(4);
  };

  // Auto-fill dari database (dipanggil dari RacikanPOSection)
  const handleAutoFill = (dbBahan, racikanItems) => {
    const updated = { ...product };
    updated.coreComponents = [...product.coreComponents.map(arr => [...arr])];

    const mapIngredient = (kategoriFilter, catKey) => {
      const items = racikanItems.filter(r => (r.kategori_bahan || r.kategori) === kategoriFilter);
      if (items.length > 0) {
        return items.map(item => {
          const inv = findInv(catKey, item.nama_bahan, item.vendor) || findInv('bahan', item.nama_bahan, item.vendor) || findInv('bibit', item.nama_bahan, item.vendor);
          const qtyNeeded = calcBibitQty(item.persentase);
          const stok = inv?.total_stock || 0;
          return {
            ...makeComp(catKey),
            item_name: item.nama_bahan,
            vendor: item.vendor || (inv?.vendor || ''),
            inventory_item_id: inv?.id || '',
            stock_available: stok,
            unit: inv?.unit || 'kg',
            qty_needed: qtyNeeded,
            status: inv ? calcStatus(stok, qtyNeeded) : 'pending',
          };
        });
      }
      // If it's a core category, keep one empty. Otherwise, empty array.
      return ['botol', 'tutup', 'spray', 'bibit'].includes(catKey) ? [makeComp(catKey)] : [];
    };

    updated.coreComponents[1] = mapIngredient('Bibit', 'bibit');
    
    // Merge Alkohol and Bahan Cair (including Aquades, DPG, PEG, Sustain, dll) into bahan_cair
    const cairanItems = racikanItems.filter(r => {
      const cat = (r.kategori_bahan || r.kategori || '').toLowerCase();
      const nama = (r.nama_bahan || r.nama || '').toLowerCase();
      return cat === 'alkohol' || cat === 'bahan cair' || cat === 'aquades' ||
             nama.includes('aquades') || nama.includes('dpg') || nama.includes('peg') || nama.includes('sustain') || nama.includes('glycol');
    });

    if (cairanItems.length > 0) {
      updated.coreComponents[4] = cairanItems.map(item => {
        const inv = findInv('bahan_cair', item.nama_bahan, item.vendor) || findInv('bahan', item.nama_bahan, item.vendor) || findInv('bibit', item.nama_bahan, item.vendor);
        const qtyNeeded = calcBibitQty(item.persentase);
        const stok = inv?.total_stock || 0;
        return {
          ...makeComp('bahan_cair'),
          item_name: item.nama_bahan,
          vendor: item.vendor || (inv?.vendor || ''),
          inventory_item_id: inv?.id || '',
          stock_available: stok,
          unit: inv?.unit || 'kg',
          qty_needed: qtyNeeded,
          status: inv ? calcStatus(stok, qtyNeeded) : 'pending',
        };
      });
    } else {
      updated.coreComponents[4] = [];
    }

    // Botol/Tutup/Spray dari DatabaseBahan
    if (dbBahan) {
      if (dbBahan.is_fullset && dbBahan.fullset_nama) {
        const inv = findInv('botol', dbBahan.fullset_nama);
        const stok = inv?.total_stock || 0;
        updated.coreComponents[0] = [{
          ...makeComp('botol'),
          item_name: dbBahan.fullset_nama,
          inventory_item_id: inv?.id || '',
          stock_available: stok,
          unit: 'pcs',
          qty_needed: qty || 0,
          status: inv ? calcStatus(stok, qty) : 'pending',
        }];
        updated.coreComponents[2] = []; // Clear tutup
        updated.coreComponents[3] = []; // Clear spray
      } else {
        if (dbBahan.botol_nama) {
          const inv = findInv('botol', dbBahan.botol_nama);
          const stok = inv?.total_stock || 0;
          updated.coreComponents[0] = [{
            ...makeComp('botol'),
            item_name: dbBahan.botol_nama,
            inventory_item_id: inv?.id || '',
            stock_available: stok,
            unit: 'pcs',
            qty_needed: qty || 0,
            status: dbBahan.botol_dari_client ? calcStatus(stok, qty, true) : (inv ? calcStatus(stok, qty) : 'pending'),
          }];
        }
        if (dbBahan.tutup_nama) {
          const inv = findInv('tutup', dbBahan.tutup_nama);
          const stok = inv?.total_stock || 0;
          updated.coreComponents[2] = [{
            ...makeComp('tutup'),
            item_name: dbBahan.tutup_nama,
            inventory_item_id: inv?.id || '',
            stock_available: stok,
            unit: 'pcs',
            qty_needed: qty || 0,
            status: inv ? calcStatus(stok, qty) : 'pending',
          }];
        }
        if (dbBahan.spray_nama) {
          const inv = findInv('spray', dbBahan.spray_nama);
          const stok = inv?.total_stock || 0;
          updated.coreComponents[3] = [{
            ...makeComp('spray'),
            item_name: dbBahan.spray_nama,
            inventory_item_id: inv?.id || '',
            stock_available: stok,
            unit: 'pcs',
            qty_needed: qty || 0,
            status: inv ? calcStatus(stok, qty) : 'pending',
          }];
        }
      }
      
      if (dbBahan.item_tambahan?.length > 0) {
        updated.coreComponents[5] = dbBahan.item_tambahan.map(it => {
          const inv = findInv('tambahan', it.nama) || findInv('bahan', it.nama);
          const qtyNeeded = (qty || 0) * (it.qty_per_pcs || 1);
          const stok = inv?.total_stock || 0;
          return {
            ...makeComp('tambahan'),
            item_name: it.nama,
            inventory_item_id: inv?.id || '',
            stock_available: stok,
            unit: inv?.unit || 'pcs',
            qty_needed: qtyNeeded,
            status: it.dari_client ? calcStatus(stok, qtyNeeded, true) : (inv ? calcStatus(stok, qtyNeeded) : 'pending'),
            item_notes: it.catatan || ''
          };
        });
      }

      // Auto-fill ukuran botol dari db bahan
      if (dbBahan.botol_ukuran_label_ml && !product.ukuran_botol_ml) {
        updated.ukuran_botol_ml = dbBahan.botol_ukuran_label_ml;
      }
    }

    onChange(updated);

    // Deteksi item yang tidak ditemukan di inventori
    const missing = [];
    
    // Cek bibit dari racikan
    racikanItems.forEach(bibit => {
      const inv = findInv('bibit', bibit.nama_bahan) || findInv('bahan', bibit.nama_bahan);
      if (!inv) {
        const qtyNeeded = calcBibitQty(bibit.persentase);
        missing.push({
          name: bibit.nama_bahan,
          category: bibit.kategori_bahan || 'bibit',
          qty_needed: qtyNeeded,
          unit: 'kg',
          vendor: bibit.vendor || '',
        });
      }
    });

    // Cek packaging dari dbBahan
    if (dbBahan) {
      if (dbBahan.is_fullset && dbBahan.fullset_nama && !findInv('botol', dbBahan.fullset_nama)) {
        missing.push({ name: dbBahan.fullset_nama, category: 'botol', qty_needed: qty || 0, unit: 'pcs', vendor: '' });
      } else if (!dbBahan.is_fullset) {
        if (dbBahan.botol_nama && !findInv('botol', dbBahan.botol_nama)) {
          missing.push({ name: dbBahan.botol_nama, category: 'botol', qty_needed: qty || 0, unit: 'pcs', vendor: '' });
        }
        if (dbBahan.tutup_nama && !findInv('tutup', dbBahan.tutup_nama)) {
          missing.push({ name: dbBahan.tutup_nama, category: 'tutup', qty_needed: qty || 0, unit: 'pcs', vendor: '' });
        }
        if (dbBahan.spray_nama && !findInv('spray', dbBahan.spray_nama)) {
          missing.push({ name: dbBahan.spray_nama, category: 'spray', qty_needed: qty || 0, unit: 'pcs', vendor: '' });
        }
      }
      if (dbBahan.item_tambahan?.length > 0) {};
    }

    setMissingItems(missing);
  };

  // Sync dengan inventory: auto-link by name/id, update stock, qty_needed & status
  useEffect(() => {
    if (!inventoryItems || inventoryItems.length === 0) return;

    let hasChanges = false;
    const enrichedComps = product.coreComponents.map((catComps, catIdx) => {
      const cat = CORE_CATEGORIES[catIdx];
      return catComps.map(comp => {
        // Abaikan baris kosong
        if (!comp.inventory_item_id && !comp.item_name) return comp;

        // Cari item di inventori
        let inv = null;
        if (comp.inventory_item_id) {
          inv = inventoryItems.find(i => i.id === comp.inventory_item_id);
        }
        if (!inv && comp.item_name) {
          inv = inventoryItems.find(i => i.item_name && i.item_name.toLowerCase() === comp.item_name.toLowerCase());
        }

        // Hitung Kebutuhan Qty
        let qtyNeeded = comp.qty_needed || 0;
        if (cat === 'bibit') {
          const bibitInRacikan = product.racikanList.find(r => r.kategori_bahan === 'Bibit' && r.nama_bahan === (comp.item_name || (inv ? inv.item_name : '')));
          if (bibitInRacikan && ukuranMl > 0) {
            qtyNeeded = +(((parseFloat(String(bibitInRacikan.persentase).replace(',', '.')) || 0) / 100) * ukuranMl * qty / 1000).toFixed(4);
          }
        } else if (cat === 'bahan_cair') {
          const cairInRacikan = product.racikanList.find(r => (r.nama_bahan || r.nama) === (comp.item_name || (inv ? inv.item_name : '')));
          if (cairInRacikan && ukuranMl > 0 && cairInRacikan.persentase) {
            qtyNeeded = +(((parseFloat(String(cairInRacikan.persentase).replace(',', '.')) || 0) / 100) * ukuranMl * qty / 1000).toFixed(4);
          }
        } else {
          qtyNeeded = qty;
        }

        const priorUsage = priorUsageMap[inv ? inv.id : comp.inventory_item_id] || 0;
        const rawStock = inv ? (inv.total_stock || 0) : (comp._raw_stock !== undefined ? comp._raw_stock : (comp.stock_available + priorUsage || 0));
        const newStock = Math.max(0, rawStock - priorUsage); // Sisa stok aktual untuk variant ini

        // Jika belum dilink ke inventori, biarkan pending. Kecuali memang pending_client
        const isClientItem = comp.status === 'pending_client' || comp.status === 'ready_client' || (inv && inv.dari_client);
        const newStatus = isClientItem 
          ? calcStatus(newStock, qtyNeeded, true)
          : (inv || comp.inventory_item_id) ? calcStatus(newStock, qtyNeeded) : 'pending';

        if (
          (inv && comp.inventory_item_id !== inv.id) ||
          comp.stock_available !== newStock ||
          comp.status !== newStatus ||
          comp.qty_needed !== qtyNeeded ||
          comp._raw_stock !== rawStock ||
          (inv && comp.item_name !== inv.item_name)
        ) {
          hasChanges = true;
          return {
            ...comp,
            inventory_item_id: inv ? inv.id : comp.inventory_item_id,
            item_name: inv ? inv.item_name : comp.item_name,
            _raw_stock: rawStock,
            stock_available: newStock,
            unit: inv ? (inv.unit || comp.unit) : comp.unit,
            qty_needed: qtyNeeded,
            status: newStatus
          };
        }
        return comp;
      });
    });

    if (hasChanges) {
      onChange({ ...product, coreComponents: enrichedComps });
    }
  }, [inventoryItems, product.target_qty, product.ukuran_botol_ml, product.racikanList, JSON.stringify(priorUsageMap)]); // eslint-disable-line

  const updateField = (key, val) => onChange({ ...product, [key]: val });

  const setRacikanList = (fn) => {
    const newList = typeof fn === 'function' ? fn(product.racikanList) : fn;
    onChange({ ...product, racikanList: newList });
  };

  const addCoreComp = (catIdx) => {
    const updated = product.coreComponents.map((arr, i) =>
      i === catIdx ? [...arr, makeComp(CORE_CATEGORIES[catIdx])] : arr
    );
    onChange({ ...product, coreComponents: updated });
  };

  const removeCoreComp = (catIdx, compIdx) => {
    const updated = product.coreComponents.map((arr, i) => {
      if (i !== catIdx) return arr;
      const filtered = arr.filter((_, ci) => ci !== compIdx);
      return filtered.length === 0 ? [makeComp(CORE_CATEGORIES[catIdx])] : filtered;
    });
    onChange({ ...product, coreComponents: updated });
  };

  const updateCoreComp = (catIdx, compIdx, updatedComp) => {
    let updated = product.coreComponents.map((arr, i) =>
      i === catIdx ? arr.map((c, ci) => ci === compIdx ? updatedComp : c) : arr
    );

    // Auto-hide Tutup and Spray if Botol is set to a Fullset
    if (catIdx === 0 && compIdx === 0) {
      const inv = inventoryItems.find(i => i.id === updatedComp.inventory_item_id);
      if (inv && inv.is_fullset) {
        updated[2] = []; // Clear Tutup
        updated[3] = []; // Clear Spray
      } else {
        // Jika berubah dari fullset ke botol biasa, pastikan Tutup dan Spray muncul lagi
        if (!updated[2] || updated[2].length === 0) updated[2] = [makeComp('tutup')];
        if (!updated[3] || updated[3].length === 0) updated[3] = [makeComp('spray')];
      }
    }

    onChange({ ...product, coreComponents: updated });
  };

  const safeCoreComponents = CORE_CATEGORIES.map((cat, catIdx) => {
    return product.coreComponents?.[catIdx] || (['botol', 'tutup', 'spray', 'bibit'].includes(cat) ? [makeComp(cat)] : []);
  });

  const allFlat = product.coreComponents.flat();
  const needBuyCount = allFlat.filter(c => c.status === 'need_buy').length;

  const queryClient = useQueryClient();
  const addInventoryItem = useMutation({
    mutationFn: (data) => base44.entities.InventoryItem.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory'] }); toast.success('Item baru ditambahkan'); },
  });

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-muted/40 border-b border-border">
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
          {index + 1}
        </div>
        <Package className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="font-semibold text-sm flex-1">
          {product.product_name || `Produk ${index + 1}`}
        </span>
        {product.target_qty && (
          <Badge variant="secondary" className="text-xs">{product.target_qty} pcs</Badge>
        )}
        {needBuyCount > 0 && (
          <Badge className="text-xs bg-red-100 text-red-700 border-red-200">{needBuyCount} perlu beli</Badge>
        )}
        <button type="button" onClick={() => setCollapsed(v => !v)} className="text-muted-foreground hover:text-foreground">
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
        <button type="button" onClick={onRemove} className="text-destructive/50 hover:text-destructive ml-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Nama Produk</Label>
              <Input className="mt-1 h-9" value={product.product_name} onChange={e => updateField('product_name', e.target.value)} placeholder="Opsional" />
            </div>
            <div>
              <Label className="text-xs">Target Qty *</Label>
              <Input type="number" className="mt-1 h-9" value={product.target_qty} onChange={e => updateField('target_qty', e.target.value)} placeholder="1000" />
            </div>
            <div>
              <Label className="text-xs">Ukuran Botol (ml)</Label>
              <Input type="number" min="1" className="mt-1 h-9" value={product.ukuran_botol_ml} onChange={e => updateField('ukuran_botol_ml', e.target.value)} placeholder="30" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={product.is_dynamic_qty || false} onCheckedChange={v => updateField('is_dynamic_qty', v)} />
            <Label className="font-normal text-xs">Dynamic Qty (ikuti sisa racikan)</Label>
          </div>

          {/* Racikan */}
          <RacikanPOSection
            ukuranMl={ukuranMl}
            qty={qty}
            racikanList={product.racikanList}
            setRacikanList={setRacikanList}
            onAutoFillComponents={handleAutoFill}
            onProductNameChange={(name) => {
              if (!product.product_name) updateField('product_name', name);
            }}
            onUkuranChange={(ml) => {
              if (!product.ukuran_botol_ml) updateField('ukuran_botol_ml', ml);
            }}
          />

          {/* Missing items alert */}
          <MissingItemsAlert
            missingItems={missingItems}
            onAddItem={async (itemData) => {
              const newItem = await addInventoryItem.mutateAsync(itemData);
              return newItem;
            }}
          />

          {/* Komponen Utama */}
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Komponen Utama</p>
              <p className="text-xs text-muted-foreground mt-0.5">Pilih item dari inventori. Status otomatis dihitung dari stok.</p>
            </div>
            {safeCoreComponents.map((catComps, catIdx) => {
              if (catComps.length === 0) return null; // Hide empty categories
              const catName = CORE_CATEGORIES[catIdx];
              return (
                <div key={catIdx} className="space-y-2">
                  {catComps.map((comp, compIdx) => (
                    <POComponentItem
                      key={`${catName}-${compIdx}`}
                      comp={comp}
                      inventoryItems={inventoryItems}
                      onChange={(updated) => updateCoreComp(catIdx, compIdx, updated)}
                      onRemove={() => removeCoreComp(catIdx, compIdx)}
                      isCore={['botol', 'tutup', 'spray'].includes(comp.category) && compIdx === 0}
                      onAddNewItem={(newItemData) => addInventoryItem.mutateAsync(newItemData)}
                      brandName={brandName}
                    />
                  ))}
                  <Button type="button" variant="ghost" size="sm"
                    className="text-xs text-muted-foreground hover:text-primary gap-1 h-7 px-2"
                    onClick={() => addCoreComp(catIdx)}>
                    <Plus className="w-3 h-3" />
                    Tambah {catName.replace('_', ' ').charAt(0).toUpperCase() + catName.replace('_', ' ').slice(1)} Lain
                  </Button>
                </div>
              );
            })}

            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-dashed border-border">
              {safeCoreComponents.map((catComps, catIdx) => {
                if (catComps.length > 0) return null;
                const catName = CORE_CATEGORIES[catIdx];
                return (
                  <Button key={`add-${catIdx}`} type="button" variant="outline" size="sm" onClick={() => addCoreComp(catIdx)} className="text-xs text-muted-foreground border-dashed h-7">
                    <Plus className="w-3 h-3 mr-1" /> Tambah {catName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}